<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { VMenu } from 'vuetify/lib/components/index.mjs'

import TextWithUrlToLink from '@/components/atoms/TextWithUrlToLink/index.vue'
import ABaseIcon from '@/components/atoms/ABaseIcon/index.vue'
import ASubTooltip from '@/components/atoms/ASubTooltip/index.vue'
import IconQuestionTooltip from '@/assets/images/icon-question-tooltip.svg'

const { t } = useI18n()

/**
 * Props
 */
export type Props = {
  tip: string
  position?: 'bottom' | 'bottom-left' | 'bottom-right' | 'right' | 'top' | 'top-right' | 'fixed'
  attach?: boolean | string
  icon?: string
  zIndex?: number
  contained?: boolean
  openedFromStart?: boolean
  openOnHover?: boolean
  openDelay?: string | number
  closeDelay?: string | number
  persistent?: boolean
  activatorClass?: string
  disabled?: boolean
  contentMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  tip: '',
  position: 'bottom-left',
  attach: undefined,
  icon: IconQuestionTooltip,
  zIndex: 2000,
  contained: false,
  openedFromStart: false,
  openOnHover: undefined,
  openDelay: undefined,
  closeDelay: undefined,
  persistent: false,
  activatorClass: undefined,
  disabled: undefined,
  contentMessage: 'upload_common.help',
})

/**
 * Refs
 */
const componentRef = ref<HTMLElement>()

/**
 * Computed
 */
const location = computed(() => {
  if (props.position === 'bottom-right') {
    return 'bottom right'
  }
  return (props.position === 'top' || props.position === 'fixed') ? 'center' : 'bottom'
})

/**
 * Lifecycle
 */
onMounted(() => {
  if (!props.openedFromStart) {
    return
  }

  if (!componentRef.value) {
    return
  }

  const buttonElement = componentRef.value.querySelector(
    '.tooltip-toggle-button',
  )

  if (!buttonElement || !(buttonElement instanceof HTMLElement)) {
    return
  }

  buttonElement.click()
})
</script>

<template>
  <div
    ref="componentRef"
    class="tooltip"
    :class="{ fixed: props.position === 'fixed', [props.position]: true }"
  >
    <VMenu
      scroll-strategy="none"
      :attach="props.attach"
      :z-index="props.zIndex"
      :location="location"
      :contained="props.contained"
      :persistent="props.persistent"
      :no-click-animation="true"
      :open-on-hover="props.openOnHover"
      :open-delay="props.openDelay"
      :close-delay="props.closeDelay"
      :disabled="props.disabled"
    >
      <template #activator="{ props: activatorProps }">
        <template v-if="$slots.activator">
          <slot
            v-if="$slots.activatorBefore"
            name="activatorBefore"
          />
          <div
            v-bind="activatorProps"
            class="tooltip-toggle-button"
            :class="activatorClass"
          >
            <slot name="activator" />
          </div>
          <slot
            v-if="$slots.activatorAfter"
            name="activatorAfter"
          />
        </template>
        <div
          v-else
          v-bind="activatorProps"
          class="tooltip-toggle-button"
          :class="activatorClass"
        >
          <ASubTooltip
            :z-index="props.zIndex"
            :is-help="true"
          >
            <template #content>
              {{ t(props.contentMessage) }}
            </template>
            <template #item>
              <ABaseIcon
                class="icon"
                :src="props.icon"
              />
            </template>
          </ASubTooltip>
        </div>
      </template>

      <div
        class="tooltip-popup"
        :data-position="position"
      >
        <template v-if="$slots.balloon">
          <slot name="balloon" />
        </template>

        <div
          v-else
          class="balloon"
        >
          <TextWithUrlToLink :text="props.tip" />
        </div>
      </div>
    </VMenu>
  </div>
</template>

<style scoped lang="scss">
.tooltip {
  position: relative;
  display: flex;

  .icon {
    position: relative;
    top: -1px;
    width: 20px;
    height: 20px;
    line-height: 0;
    cursor: pointer;
    opacity: 1;

    &:hover {
      opacity: 0.6;
    }
  }
}

.tooltip-popup {
  position: absolute;

  &[data-position='top'] {
    top: -30px;
    left: 0;
    transform: translateX(-50%) translateY(-100%);
  }

  &[data-position='top-right'] {
    top: -40px;
    left: 120px;
    transform: translateX(-50%) translateY(-100%);
  }

  &[data-position='right'] {
    top: 50%;
    left: 25px;
    transform: translateY(calc(-50% - 10px));
  }

  &[data-position='bottom'] {
    bottom: -10px;
    left: 0;
    transform: translateX(-50%) translateY(100%);
  }

  &[data-position='bottom-left'] {
    top: calc(100% + 10px);
    right: 0; // 注: 名前と違い、left じゃなくて right になっている
  }

  &[data-position='bottom-right'] {
    top: calc(100% + 10px);
    right: 0;
  }

  &[data-position='fixed'] {
    position: fixed;
    inset: var(--ATooltip-PostionTop, unset) var(--ATooltip-PostionRight, unset) var(--ATooltip-PostionBottom, unset) var(--ATooltip-PostionLeft, unset);
    transform: none;
  }

  > .balloon {
    width: max-content;
    max-width: var(--ATooltip--maxWidth, 350px);
    padding: 15px 17px;
    font-size: 13px;
    line-height: 2;
    color: rgb(var(--v-theme-ui-black));
    white-space: pre-wrap;
    background-color: rgb(var(--v-theme-ui-white));
    border-radius: 4px;
    box-shadow: 0 0 8px rgba(var(--v-theme-ui-black), 16%);
  }
}

.tooltip.bottom-right {
  // HACK: デフォルトの挙動の left 基準にすると、予期せぬ時(ex: line-height 指定時)などに、初回描画の位置がおかしくなるので調整。
  .v-overlay__content, :deep(.v-overlay__content) {
    left: unset;
    right: 0;
  }
}
</style>
